import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, ArrowLeft } from "lucide-react";
import ColoringActivity from "./ColoringActivity";
import MatchingGameActivity from "./MatchingGameActivity";
import PuzzleActivity from "./PuzzleActivity";

interface StoryPrompt {
  start: string;
  hint: string;
  emoji: string;
}

const STORIES: StoryPrompt[] = [
  { start: "في يوم من الأيام، وجد أرنوب صندوقاً سحرياً في الحديقة...", hint: "ماذا كان داخل الصندوق؟ ماذا حصل بعد ذلك؟", emoji: "🐰" },
  { start: "استيقظت السمكة الصغيرة ووجدت أن البحر تحول للون الوردي...", hint: "لماذا تغير لون البحر؟ ماذا فعلت السمكة؟", emoji: "🐟" },
  { start: "كان الروبوت الصغير يحلم بأن يتعلم الطيران...", hint: "كيف حقق حلمه؟ من ساعده؟", emoji: "🤖" },
  { start: "في غابة بعيدة، وجدت القطة الصغيرة خريطة كنز قديمة...", hint: "إلى أين قادتها الخريطة؟ ماذا وجدت؟", emoji: "🐱" },
  { start: "طار البالون الأحمر عالياً جداً حتى وصل إلى القمر...", hint: "ماذا رأى على القمر؟ هل قابل أحداً؟", emoji: "🎈" },
  { start: "اكتشف الفيل الصغير أنه يستطيع التحدث مع الأشجار...", hint: "ماذا أخبرته الأشجار؟ ما السر الذي كشفته؟", emoji: "🐘" },
];

const COLORS = [
  { name: "أحمر", color: "bg-red-400", emoji: "❤️" },
  { name: "أزرق", color: "bg-blue-400", emoji: "💙" },
  { name: "أخضر", color: "bg-green-400", emoji: "💚" },
  { name: "أصفر", color: "bg-yellow-400", emoji: "💛" },
  { name: "بنفسجي", color: "bg-purple-400", emoji: "💜" },
  { name: "برتقالي", color: "bg-orange-400", emoji: "🧡" },
];

const FEELINGS = [
  { emoji: "😊", label: "سعيد", color: "border-yellow-400 bg-yellow-50" },
  { emoji: "😢", label: "حزين", color: "border-blue-400 bg-blue-50" },
  { emoji: "😡", label: "غاضب", color: "border-red-400 bg-red-50" },
  { emoji: "😨", label: "خائف", color: "border-purple-400 bg-purple-50" },
  { emoji: "🥰", label: "محبوب", color: "border-pink-400 bg-pink-50" },
  { emoji: "😌", label: "هادئ", color: "border-green-400 bg-green-50" },
];

interface Props {
  onComplete: (score: number, stars: number, activityKey: string) => void;
  onBack: () => void;
}

type SubActivity = "choose" | "story" | "colors" | "feelings" | "coloring" | "matching" | "puzzle";

export default function CreativeActivity({ onComplete, onBack }: Props) {
  const [subActivity, setSubActivity] = useState<SubActivity>("choose");
  const [storyIdx, setStoryIdx] = useState(0);
  const [storyText, setStoryText] = useState("");
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [selectedFeeling, setSelectedFeeling] = useState<string | null>(null);
  const [feelingDesc, setFeelingDesc] = useState("");
  const [finished, setFinished] = useState(false);
  const [result, setResult] = useState({ score: 0, stars: 0 });

  const handleFinishStory = () => {
    const wordCount = storyText.trim().split(/\s+/).filter(Boolean).length;
    const score = Math.min(10, Math.round(wordCount / 3));
    const stars = score >= 8 ? 3 : score >= 5 ? 2 : 1;
    setResult({ score, stars });
    setFinished(true);
    onComplete(score, stars, "creative_story");
  };

  const toggleColor = (name: string) => {
    setSelectedColors(prev =>
      prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]
    );
  };

  const handleFinishColors = () => {
    const score = Math.min(10, selectedColors.length * 2);
    const stars = selectedColors.length >= 4 ? 3 : selectedColors.length >= 2 ? 2 : 1;
    setResult({ score, stars });
    setFinished(true);
    onComplete(score, stars, "creative_colors");
  };

  const handleFinishFeelings = () => {
    const wordCount = feelingDesc.trim().split(/\s+/).filter(Boolean).length;
    const score = selectedFeeling ? Math.min(10, 5 + Math.round(wordCount / 2)) : 0;
    const stars = score >= 8 ? 3 : score >= 5 ? 2 : 1;
    setResult({ score, stars });
    setFinished(true);
    onComplete(score, stars, "creative_feelings");
  };

  if (finished) {
    return (
      <Card className="text-center border-2 border-pink-300 bg-gradient-to-b from-pink-50 to-white">
        <CardContent className="p-8 space-y-4">
          <div className="text-6xl">🎨</div>
          <h2 className="text-2xl font-bold text-foreground">إبداع رائع!</h2>
          <div className="flex justify-center gap-1">
            {[1, 2, 3].map(s => (
              <Star key={s} className={`h-10 w-10 ${s <= result.stars ? "text-yellow-400 fill-yellow-400" : "text-muted"}`} />
            ))}
          </div>
          <p className="text-lg text-muted-foreground">حصلت على {result.score} نقاط</p>
          <Button onClick={onBack} variant="outline">
            <ArrowLeft className="h-4 w-4 ml-2" /> العودة للأنشطة
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Delegate to sub-activity components
  if (subActivity === "coloring") {
    return <ColoringActivity onComplete={onComplete} onBack={() => setSubActivity("choose")} />;
  }
  if (subActivity === "matching") {
    return <MatchingGameActivity onComplete={onComplete} onBack={() => setSubActivity("choose")} />;
  }
  if (subActivity === "puzzle") {
    return <PuzzleActivity onComplete={onComplete} onBack={() => setSubActivity("choose")} />;
  }

  if (subActivity === "choose") {
    return (
      <div className="space-y-4">
        <Button onClick={onBack} variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 ml-1" /> رجوع
        </Button>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { key: "story" as const, emoji: "📖", title: "أكمل القصة", desc: "استخدم خيالك لإكمال قصة ممتعة!", border: "border-pink-200 hover:border-pink-400" },
            { key: "colors" as const, emoji: "🎨", title: "عالم الألوان", desc: "اختر الألوان المفضلة وابنِ لوحتك!", border: "border-purple-200 hover:border-purple-400" },
            { key: "feelings" as const, emoji: "🎭", title: "ارسم مشاعرك", desc: "عبّر عن مشاعرك وصفها بكلماتك!", border: "border-cyan-200 hover:border-cyan-400" },
            { key: "coloring" as const, emoji: "🖍️", title: "التلوين التفاعلي", desc: "لوّن رسومات كرتونية جميلة!", border: "border-rose-200 hover:border-rose-400" },
            { key: "matching" as const, emoji: "🃏", title: "مطابقة الصور", desc: "اعثر على الأزواج المتطابقة!", border: "border-indigo-200 hover:border-indigo-400" },
            { key: "puzzle" as const, emoji: "🧩", title: "تركيب الأشكال", desc: "رتّب القطع في مكانها الصحيح!", border: "border-amber-200 hover:border-amber-400" },
          ].map((item, i) => (
            <Card
              key={item.key}
              className={`cursor-pointer hover:shadow-lg transition-all border-2 ${item.border} animate-fade-in`}
              style={{ animationDelay: `${i * 60}ms` }}
              onClick={() => setSubActivity(item.key)}
            >
              <CardContent className="p-5 text-center space-y-2">
                <div className="text-4xl">{item.emoji}</div>
                <h3 className="text-lg font-bold text-foreground">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (subActivity === "feelings") {
    return (
      <div className="space-y-4">
        <Button onClick={() => setSubActivity("choose")} variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 ml-1" /> رجوع
        </Button>
        <Card className="border-2 border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50">
          <CardHeader className="text-center">
            <div className="text-5xl mb-2">🎭</div>
            <CardTitle className="text-lg">كيف تحس الآن؟</CardTitle>
            <p className="text-sm text-muted-foreground">اختر المشاعر اللي تحس فيها ثم وصفها!</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              {FEELINGS.map(f => (
                <button
                  key={f.label}
                  onClick={() => setSelectedFeeling(f.label)}
                  className={`p-4 rounded-xl text-center transition-all border-2 ${
                    selectedFeeling === f.label ? `${f.color} scale-105 shadow-lg` : "border-muted bg-card"
                  }`}
                >
                  <div className="text-3xl mb-1">{f.emoji}</div>
                  <span className="text-sm font-medium">{f.label}</span>
                </button>
              ))}
            </div>
            {selectedFeeling && (
              <>
                <p className="text-sm text-muted-foreground text-center">✏️ صف لنا لماذا تحس بهذا الشعور:</p>
                <Textarea
                  value={feelingDesc}
                  onChange={e => setFeelingDesc(e.target.value)}
                  placeholder="مثلاً: أنا سعيد لأن اليوم لعبت مع أصدقائي..."
                  className="min-h-[100px] text-lg"
                  dir="rtl"
                />
                <Button onClick={handleFinishFeelings} disabled={feelingDesc.trim().length < 5} className="w-full" size="lg">
                  أرسل مشاعري 💕
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (subActivity === "story") {
    const story = STORIES[storyIdx];
    return (
      <div className="space-y-4">
        <Button onClick={() => setSubActivity("choose")} variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 ml-1" /> رجوع
        </Button>
        <Card className="border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-orange-50">
          <CardHeader className="text-center">
            <div className="text-5xl mb-2">{story.emoji}</div>
            <CardTitle className="text-lg">أكمل القصة</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-lg font-medium text-foreground leading-relaxed bg-card p-4 rounded-xl border">{story.start}</p>
            <p className="text-sm text-muted-foreground">💡 {story.hint}</p>
            <Textarea
              value={storyText}
              onChange={e => setStoryText(e.target.value)}
              placeholder="اكتب تكملة القصة هنا..."
              className="min-h-[120px] text-lg"
              dir="rtl"
            />
            <div className="flex gap-2">
              <Button onClick={() => setStoryIdx(prev => (prev + 1) % STORIES.length)} variant="outline" className="flex-1">
                قصة أخرى 🔄
              </Button>
              <Button onClick={handleFinishStory} disabled={storyText.trim().length < 10} className="flex-1">
                إرسال القصة ✨
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Colors activity
  return (
    <div className="space-y-4">
      <Button onClick={() => setSubActivity("choose")} variant="ghost" size="sm">
        <ArrowLeft className="h-4 w-4 ml-1" /> رجوع
      </Button>
      <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">🎨 اختر ألوانك المفضلة!</CardTitle>
          <p className="text-muted-foreground">اضغط على الألوان التي تحبها</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            {COLORS.map(c => (
              <button
                key={c.name}
                onClick={() => toggleColor(c.name)}
                className={`p-4 rounded-xl text-center transition-all border-2 ${
                  selectedColors.includes(c.name) ? "border-foreground scale-105 shadow-lg" : "border-muted"
                }`}
              >
                <div className={`w-12 h-12 rounded-full ${c.color} mx-auto mb-2`} />
                <span className="text-sm font-medium">{c.emoji} {c.name}</span>
              </button>
            ))}
          </div>
          {selectedColors.length > 0 && (
            <div className="p-3 rounded-xl bg-card border text-center">
              <p className="text-sm text-muted-foreground mb-1">لوحتك:</p>
              <div className="flex justify-center gap-2">
                {selectedColors.map(name => {
                  const c = COLORS.find(x => x.name === name)!;
                  return <div key={name} className={`w-8 h-8 rounded-full ${c.color}`} />;
                })}
              </div>
            </div>
          )}
          <Button onClick={handleFinishColors} disabled={selectedColors.length === 0} className="w-full" size="lg">
            تم! أنهيت لوحتي 🎉
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
